import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Compass.module.css';
import { TargetIcon, ClipboardIcon, SearchIcon, ChartIcon, BookIcon, CalculatorIcon } from './Icons';
import TrashIcon from '../assets/icons/trash.svg';
import EditIcon from '../assets/icons/edit-pencil.svg';
import { useAutoSave } from '../hooks/useAutoSave';
import { API_BASE_URL } from '../utils/apiConfig';

const Compass = () => {
  const { isAuthenticated, user } = useAuth0();
  
  // Single activities state to avoid separation issues
  const [collegeActivities, setCollegeActivities] = useState([]);
  
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const [editingActivityId, setEditingActivityId] = useState(null);

  // Auto-save activities
  useAutoSave('collegeActivities', collegeActivities);
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [validationPosition, setValidationPosition] = useState({ top: 0, left: 0 });
  const validationTimeout = useRef(null);
  
  // Form state for new activity
  const [newActivity, setNewActivity] = useState({
    name: '',
    organization: '',
    status: 'In Progress',
    gradeLevel: [],
    hoursPerWeek: '',
    weeksPerYear: '36',
    activityTier: 0,
    leadershipRole: 0,
    description: ''
  });

  // Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Clean up validation timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout.current) {
        clearTimeout(validationTimeout.current);
      }
    };
  }, []);

  // Load activities from localStorage on mount (simplified and more robust)
  useEffect(() => {
    const loadActivities = () => {
      try {
        const savedActivities = JSON.parse(localStorage.getItem('collegeActivities')) || [];
        setCollegeActivities(savedActivities);
        console.log('[Compass] Loaded activities:', savedActivities.length, 'activities');
      } catch (error) {
        console.error('[Compass] Error loading activities:', error);
        // Reset to empty state if there's an error
        localStorage.setItem('collegeActivities', JSON.stringify([]));
        setCollegeActivities([]);
      }
    };

    loadActivities();
    
    // Migrate activities to AI scoring if needed
    migrateActivitiesToAIScoring();

    // Listen for storage changes from other tabs
    const handleStorageChange = (e) => {
      if (e.key === 'collegeActivities') {
        console.log('[Compass] Storage change detected from another tab, reloading activities');
        loadActivities();
      }
    };

    // Listen for focus events to reload data when returning to the tab
    const handleWindowFocus = () => {
      console.log('[Compass] Window focused, checking for data updates');
      loadActivities();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', handleWindowFocus);

    // Cleanup listeners on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Single save effect - simplified and more reliable
  useEffect(() => {
    if (collegeActivities.length === 0) {
      // Only save empty array if there's no existing data
      const existingData = localStorage.getItem('collegeActivities');
      if (!existingData || existingData === 'null' || existingData === 'undefined') {
        localStorage.setItem('collegeActivities', JSON.stringify([]));
      }
      return;
    }

    try {
      const dataToSave = JSON.stringify(collegeActivities);
      localStorage.setItem('collegeActivities', dataToSave);
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('collegeActivitiesUpdated', {
        detail: { activities: collegeActivities }
      }));
      
      console.log('[Compass] Activities saved:', collegeActivities.length, 'activities');
    } catch (e) {
      console.error('[Compass] Failed to save activities:', e);
    }
  }, [collegeActivities]);

  // Function to show validation message
  const showValidation = (message, targetElement) => {
    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      setValidationPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
    
    setValidationMessage(message);
    setShowValidationMessage(true);
    
    // Auto-hide after 3 seconds
    if (validationTimeout.current) {
      clearTimeout(validationTimeout.current);
    }
    
    validationTimeout.current = setTimeout(() => {
      setShowValidationMessage(false);
    }, 3000);
  };

  // Calculate time commitment and longevity based on hours and weeks
  const calculateTimeCommitment = (hoursPerWeek) => {
    const hours = parseInt(hoursPerWeek);
    if (isNaN(hours)) return 0;
    
    if (hours >= 8) return 2; // High commitment
    if (hours >= 2) return 1; // Moderate commitment
    return 0; // Low commitment
  };

  const handleDeleteActivity = (id) => {
    const updatedActivities = collegeActivities.filter(activity => activity.id !== id);
    setCollegeActivities(updatedActivities);
    console.log('[Compass] Activity deleted');
  };

  const handleEditActivity = (id) => {
    // Find the activity to edit
    const activityToEdit = collegeActivities.find(activity => activity.id === id);
    
    if (activityToEdit) {
      // Convert grade level string to array if it's a string
      let gradeLevelArray = [];
      if (activityToEdit.originalGradeLevel) {
        gradeLevelArray = activityToEdit.originalGradeLevel;
      } else if (typeof activityToEdit.gradeLevel === 'string') {
        // Parse grade level string back to array
        const gradeLevelStr = activityToEdit.gradeLevel;
        const matches = gradeLevelStr.match(/(\d+|K)/g);
        if (matches) {
          gradeLevelArray = matches;
        }
      }
      
      setNewActivity({
        name: activityToEdit.name || '',
        organization: activityToEdit.organization || '',
        status: activityToEdit.status || 'In Progress',
        gradeLevel: gradeLevelArray,
        hoursPerWeek: activityToEdit.hoursPerWeek?.toString() || '',
        weeksPerYear: activityToEdit.weeksPerYear?.toString() || '36',
        activityTier: activityToEdit.activityTier || 0,
        leadershipRole: activityToEdit.leadershipRole || 0,
        description: activityToEdit.description || ''
      });
      
      setEditingActivityId(id);
      setShowEditActivityModal(true);
    }
  };

  const handleAddActivity = () => {
    setNewActivity({
      name: '',
      organization: '',
      status: 'In Progress',
      gradeLevel: [],
      hoursPerWeek: '',
      weeksPerYear: '36',
      activityTier: 0,
      leadershipRole: 0,
      description: ''
    });
    setEditingActivityId(null);
    setShowAddActivityModal(true);
  };

  const closeModal = () => {
    setShowAddActivityModal(false);
    setShowEditActivityModal(false);
    setEditingActivityId(null);
    setFormErrors({});
    setShowValidationMessage(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewActivity(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleGradeChange = (grade, isChecked) => {
    setNewActivity(prev => {
      const currentGrades = [...prev.gradeLevel];
      
      if (isChecked) {
        if (!currentGrades.includes(grade)) {
          currentGrades.push(grade);
        }
      } else {
        const index = currentGrades.indexOf(grade);
        if (index > -1) {
          currentGrades.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        gradeLevel: currentGrades
      };
    });
  };

  const renderImpactScore = (score) => {
    if (score === null || score === undefined) return 'N/A';
    
    // Score should already be on a 1-10 scale, no need to multiply by 10
    const displayScore = Math.round(score);
    let color = '#4CAF50'; // Green for high scores
    let label = 'High Impact';
    
    if (displayScore < 6) {
      color = '#FF9800'; // Orange for medium scores
      label = 'Medium Impact';
    }
    if (displayScore < 4) {
      color = '#F44336'; // Red for low scores
      label = 'Low Impact';
    }
    
    return (
      <div style={{ color, fontWeight: 'bold' }}>
        {displayScore}/10 - {label}
      </div>
    );
  };

  const calculateImpactScore = async (activity) => {
    try {
      console.log('[Compass] Calculating impact score for activity:', activity.name);
      
      const response = await fetch(`${API_BASE_URL}/analytics/impact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: activity.name,
          organization: activity.organization,
          hoursPerWeek: activity.hoursPerWeek,
          weeksPerYear: activity.weeksPerYear,
          leadershipRole: activity.leadershipRole,
          activityTier: activity.activityTier,
          description: activity.description
        }),
      });

      console.log('[Compass] API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Compass] API error response:', errorText);
        throw new Error(`Failed to calculate impact score: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log('[Compass] API response result:', result);
      
      // Ensure we have valid data
      const score = result.impact || 5.0;
      const explanation = result.justification || 'Impact score calculation unavailable.';
      
      console.log('[Compass] Processed score:', score, 'explanation:', explanation);
      
      return {
        score: score, // Backend returns 'impact', frontend expects 'score'
        explanation: explanation // Backend returns 'justification', frontend expects 'explanation'
      };
    } catch (error) {
      console.error('[Compass] Error calculating impact score:', error);
      // Return default values if API fails
      return {
        score: 5.0,
        explanation: 'Impact score calculation unavailable. Please try again later.'
      };
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!newActivity.name.trim()) {
      errors.name = 'Activity name is required';
    }
    
    if (!newActivity.organization.trim()) {
      errors.organization = 'Organization is required';
    }
    
    if (!newActivity.hoursPerWeek || newActivity.hoursPerWeek <= 0) {
      errors.hoursPerWeek = 'Hours per week must be greater than 0';
    }
    
    if (newActivity.gradeLevel.length === 0) {
      errors.gradeLevel = 'Please select at least one grade level';
    }
    
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      // Show validation message
      const firstError = Object.values(errors)[0];
      showValidation(firstError);
      return false;
    }
    
    return true;
  };

  // Handle form submission for adding new activity
  const handleSubmitActivity = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Calculate score using AI
      const impactResult = await calculateImpactScore(newActivity);
      
      // Parse numeric inputs for storage
      const hoursPerWeek = parseFloat(newActivity.hoursPerWeek);
      const weeksPerYear = parseInt(newActivity.weeksPerYear);
      
      // Format grade level as a string for display
      const formattedGradeLevel = formatGradeLevel(newActivity.gradeLevel);
      
      // Create new activity with ID, score, and explanation
      const activityToAdd = {
        ...newActivity,
        id: collegeActivities.length > 0 ? Math.max(...collegeActivities.map(a => a.id)) + 1 : 1,
        hoursPerWeek,
        weeksPerYear,
        gradeLevel: formattedGradeLevel, // Use formatted grade level for display
        originalGradeLevel: [...newActivity.gradeLevel], // Keep original array for editing
        score: impactResult.score,
        aiExplanation: impactResult.explanation
      };
      
      // Add to activities state
      const updatedActivities = [...collegeActivities, activityToAdd];
      setCollegeActivities(updatedActivities);
      
      console.log('[Compass] Activity added successfully');
      
      // Close modal and reset form
      closeModal();
    } catch (error) {
      console.error('Error adding activity:', error);
      showValidation('Failed to add activity. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle form submission for editing existing activity
  const handleSubmitEditActivity = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('[Compass] Starting edit activity submission for ID:', editingActivityId);
      console.log('[Compass] Activity data to update:', newActivity);
      
      // Calculate score using AI
      const impactResult = await calculateImpactScore(newActivity);
      console.log('[Compass] Impact calculation result:', impactResult);
      
      // Parse numeric inputs for storage
      const hoursPerWeek = parseFloat(newActivity.hoursPerWeek);
      const weeksPerYear = parseInt(newActivity.weeksPerYear);
      
      // Format grade level as a string for display
      const formattedGradeLevel = formatGradeLevel(newActivity.gradeLevel);
      
      // Update the activities list
      const updatedActivities = collegeActivities.map(activity => 
        activity.id === editingActivityId 
          ? { 
              ...newActivity, 
              id: editingActivityId,
              hoursPerWeek,
              weeksPerYear,
              gradeLevel: formattedGradeLevel, // Use formatted grade level for display
              originalGradeLevel: [...newActivity.gradeLevel], // Keep original array for editing
              score: impactResult.score,
              aiExplanation: impactResult.explanation
            } 
          : activity
      );
      
      console.log('[Compass] Updated activities list:', updatedActivities);
      console.log('[Compass] Updated activity score:', updatedActivities.find(a => a.id === editingActivityId)?.score);
      
      setCollegeActivities(updatedActivities);
      
      console.log('[Compass] Activity edited successfully');
      
      // Close modal and reset form
      closeModal();
    } catch (error) {
      console.error('[Compass] Error editing activity:', error);
      showValidation('Failed to edit activity. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to format grade levels for display
  const formatGradeLevel = (gradeLevels) => {
    if (!gradeLevels || gradeLevels.length === 0) return '';
    
    // Sort grades
    const sortedGrades = [...gradeLevels].sort((a, b) => {
      if (a === 'K') return -1;
      if (b === 'K') return 1;
      return parseInt(a) - parseInt(b);
    });
    
    // Find consecutive ranges
    const ranges = [];
    let start = sortedGrades[0];
    let end = start;
    
    for (let i = 1; i < sortedGrades.length; i++) {
      const current = sortedGrades[i];
      const prev = sortedGrades[i-1];
      
      // Check if current is consecutive to previous
      // Special case for K and 1
      const isConsecutive = 
        (prev === 'K' && current === '1') || 
        (prev !== 'K' && parseInt(current) === parseInt(prev) + 1);
      
      if (isConsecutive) {
        end = current;
      } else {
        // Add the previous range
        ranges.push(start === end ? start : `${start}-${end}`);
        // Start a new range
        start = current;
        end = current;
      }
    }
    
    // Add the last range
    ranges.push(start === end ? start : `${start}-${end}`);
    
    // Calculate total academic years based on the ranges
    let totalYears = 0;
    
    ranges.forEach(range => {
      if (range.includes('-')) {
        const [rangeStart, rangeEnd] = range.split('-');
        if (rangeStart === 'K') {
          totalYears += parseInt(rangeEnd) + 1;
        } else {
          totalYears += parseInt(rangeEnd) - parseInt(rangeStart) + 1;
        }
      } else {
        totalYears += 1;
      }
    });
    
    const yearText = totalYears > 1 ? `${totalYears} academic years` : '1 academic year';
    
    return `${ranges.join(', ')} (${yearText})`;
  };

  // Helper function to get the appropriate suffix for grade numbers
  const getGradeSuffix = (grade) => {
    if (grade === 1) return 'st';
    if (grade === 2) return 'nd';
    if (grade === 3) return 'rd';
    return 'th';
  };

  // Render activity form fields for both Add and Edit modals
  const renderActivityFormFields = (handleSubmit, buttonText) => {
    return (
      <form className={styles.activityForm} onSubmit={handleSubmit} noValidate>
        <div className={styles.formRow}>
          <div className={`${styles.formGroup} ${formErrors.name ? styles.hasError : ''}`}>
            <label htmlFor="name">Activity Name*</label>
            <input 
              type="text" 
              name="name"
              value={newActivity.name}
              onChange={handleInputChange}
              placeholder="Enter activity name" 
            />
            {formErrors.name && (
              <div className={`${styles.errorMessage} ${styles.visible}`}>
                {formErrors.name}
              </div>
            )}
          </div>
          <div className={`${styles.formGroup} ${formErrors.organization ? styles.hasError : ''}`}>
            <label htmlFor="organization">Organization*</label>
            <input 
              type="text" 
              name="organization"
              value={newActivity.organization}
              onChange={handleInputChange}
              placeholder="Enter organization name" 
            />
            {formErrors.organization && (
              <div className={`${styles.errorMessage} ${styles.visible}`}>
                {formErrors.organization}
              </div>
            )}
          </div>
        </div>
        
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="status">Status</label>
            <select 
              name="status"
              value={newActivity.status}
              onChange={handleInputChange}
            >
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Planned">Planned</option>
            </select>
          </div>
          <div className={`${styles.formGroup} ${formErrors.gradeLevel ? styles.hasError : ''}`}>
            <label htmlFor="gradeLevel">Grade Level* (Select all that apply)</label>
            <div className={styles.checkboxGroup}>
              <div className={styles.gradeGrid}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(grade => (
                  <div key={grade} className={styles.checkboxItem}>
                    <input 
                      type="checkbox" 
                      id={`grade${grade}`} 
                      checked={newActivity.gradeLevel?.includes(grade.toString())}
                      onChange={(e) => handleGradeChange(grade.toString(), e.target.checked)}
                    />
                    <label htmlFor={`grade${grade}`}>{grade}{getGradeSuffix(grade)}</label>
                  </div>
                ))}
              </div>
            </div>
            {formErrors.gradeLevel && (
              <div className={`${styles.errorMessage} ${styles.visible}`}>
                {formErrors.gradeLevel}
              </div>
            )}
            <div className={styles.helperText}>
              Each grade represents one academic year (mid-August to late May)
            </div>
          </div>
        </div>
        
        <h3 className={styles.formSectionTitle}>Activity Details</h3>
        
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="activityTier">Activity Tier (Prestige)</label>
            <select 
              name="activityTier"
              value={newActivity.activityTier}
              onChange={handleInputChange}
            >
              <option value="0">0: Common school-level participation</option>
              <option value="1">1: Significant state/regional honors</option>
              <option value="2">2: Rare national/international achievement</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="leadershipRole">Leadership Role</label>
            <select 
              name="leadershipRole"
              value={newActivity.leadershipRole}
              onChange={handleInputChange}
            >
              <option value="0">0: No leadership (member only)</option>
              <option value="1">1: Moderate leadership (officer position)</option>
              <option value="2">2: Top leadership (founder, president)</option>
            </select>
          </div>
        </div>
        
        <div className={styles.formRow}>
          <div className={`${styles.formGroup} ${formErrors.hoursPerWeek ? styles.hasError : ''}`}>
            <label htmlFor="hoursPerWeek">Hours Per Week*</label>
            <input 
              type="number" 
              name="hoursPerWeek"
              value={newActivity.hoursPerWeek}
              onChange={handleInputChange}
              min="0.5" 
              step="0.5"
              placeholder="Average hours per week during the school year"
            />
            {formErrors.hoursPerWeek && (
              <div className={`${styles.errorMessage} ${styles.visible}`}>
                {formErrors.hoursPerWeek}
              </div>
            )}
            <div className={styles.helperText}>
              Average weekly hours when the activity is in session
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="weeksPerYear">Weeks Per Year* (School year: ~36 weeks)</label>
            <input 
              type="number" 
              name="weeksPerYear"
              value={newActivity.weeksPerYear}
              onChange={handleInputChange}
              min="1" 
              max="52"
              placeholder="Typical school year is 36 weeks"
            />
            <div className={styles.helperText}>
              A typical school year (mid-August to late May) is about 36 weeks
            </div>
          </div>
        </div>
        
        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
          <label htmlFor="description">Activity Description</label>
          <textarea 
            name="description"
            value={newActivity.description}
            onChange={handleInputChange}
            placeholder="Describe your role, responsibilities, and achievements in this activity..."
            rows="5"
          />
          <div className={styles.descriptionNote}>
            <p>Note: Descriptions will be evaluated based on their content and actual impact, not on writing quality (vocabulary, spelling, grammar).</p>
          </div>
        </div>
        
        <div className={styles.formActions}>
          <button type="button" className={styles.cancelButton} onClick={closeModal} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? 'Calculating...' : buttonText}
          </button>
        </div>
      </form>
    );
  };

  // Migrate existing activities to use AI scoring
  const migrateActivitiesToAIScoring = async () => {
    const activities = JSON.parse(localStorage.getItem('collegeActivities')) || [];
    const needsMigration = activities.filter(activity => !activity.aiExplanation);
    
    if (needsMigration.length === 0) return;
    
    console.log(`[Compass] Migrating ${needsMigration.length} activities to AI scoring...`);
    
    const updatedActivities = [...activities];
    
    for (let i = 0; i < needsMigration.length; i++) {
      const activity = needsMigration[i];
      try {
        const impactResult = await calculateImpactScore(activity);
        
        // Find and update the activity in the array
        const index = updatedActivities.findIndex(a => a.id === activity.id);
        if (index !== -1) {
          updatedActivities[index] = {
            ...updatedActivities[index],
            score: impactResult.score,
            aiExplanation: impactResult.explanation
          };
        }
        
        console.log(`[Compass] Migrated activity: ${activity.name}`);
      } catch (error) {
        console.error(`[Compass] Failed to migrate activity ${activity.name}:`, error);
      }
    }
    
    // Save updated activities to localStorage
    try {
      localStorage.setItem('collegeActivities', JSON.stringify(updatedActivities));
      console.log('[Compass] Activities migration completed');
    } catch (e) {
      console.error('[Compass] Failed to save migrated activities:', e);
    }
  };

  return (
    <>
      <div className={styles.compassContainer}>
        <div className={styles.contentLayout}>
          {/* Left Sidebar */}
          <div className={styles.sidebar}>
            <div className={styles.overviewCard}>
              <h2>College Activity Overview</h2>
              
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Total Activities</span>
                <span className={styles.statValue}>{collegeActivities.length}</span>
              </div>
              
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Total Hours Completed</span>
                <span className={styles.statValue}>{Math.round(collegeActivities.reduce((sum, activity) => {
                  return sum + (activity.hoursPerWeek * activity.weeksPerYear);
                }, 0))}</span>
              </div>
            </div>
            
          </div>
          
          {/* Main Content */}
          <div className={styles.mainContent}>
            <div className={styles.tableHeader}>
              <h2>Your College Activities</h2>
              <div className={styles.headerActions}>
                <button 
                  className={styles.addButton}
                  onClick={handleAddActivity}
                >
                  + Add Activity
                </button>
              </div>
            </div>
            
            <div className={styles.tableContainer}>
              <table className={styles.activitiesTable}>
                <thead>
                  <tr>
                    <th>Activity Name</th>
                    <th>Organization</th>
                    <th>Status</th>
                    <th>Grade Level</th>
                    <th>Hours/Week</th>
                    <th>Weeks/Year</th>
                    <th>Impact Score</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {collegeActivities.map(activity => (
                    <tr key={activity.id}>
                      <td>{activity.name}</td>
                      <td>{activity.organization}</td>
                      <td>
                        <span className={styles.statusBadge}>
                          {activity.status}
                        </span>
                      </td>
                      <td>{Array.isArray(activity.gradeLevel) ? activity.gradeLevel.join(', ') : activity.gradeLevel}</td>
                      <td>{activity.hoursPerWeek}</td>
                      <td>{activity.weeksPerYear}</td>
                      <td>{renderImpactScore(activity.score)}</td>
                      <td>
                        <div className={styles.actionButtons}>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleEditActivity(activity.id)}
                            aria-label="Edit activity"
                          >
                            <img src={EditIcon} alt="Edit" className={styles.actionIcon} />
                          </button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteActivity(activity.id)}
                            aria-label="Delete activity"
                          >
                            <img src={TrashIcon} alt="Delete" className={styles.actionIcon} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    
      {/* Add Activity Modal - Rendered as Portal */}
      {showAddActivityModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${styles.active}`}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Add New College Activity</h2>
              <button className={styles.closeButton} onClick={closeModal}>✕</button>
            </div>
            <div className={styles.modalBody}>
              {renderActivityFormFields(handleSubmitActivity, "Save Activity")}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Edit Activity Modal - Rendered as Portal */}
      {showEditActivityModal && ReactDOM.createPortal(
        <div className={`${styles.modalOverlay} ${styles.active}`}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Edit College Activity</h2>
              <button className={styles.closeButton} onClick={closeModal}>✕</button>
            </div>
            <div className={styles.modalBody}>
              {renderActivityFormFields(handleSubmitEditActivity, "Update Activity")}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Validation popup */}
      {showValidationMessage && (
        <div 
          className={`${styles.validationPopup} ${styles.visible}`}
          style={{
            top: `${validationPosition.top}px`,
            left: `${validationPosition.left}px`
          }}
        >
          {validationMessage}
        </div>
      )}
    </>
  );
};

export default Compass; 